"use client";

import {
	useCallback,
	useEffect,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";

export const TIMED_UNDO_DEFAULT_DURATION_MS = 5_000;

export interface TimedUndoPending<T> {
	payload: T;
	/** Epoch ms when the window started. */
	startedAt: number;
	/** Epoch ms when the window ends and `onCommit` runs. */
	endsAt: number;
}

export interface UseTimedUndoOptions<T> {
	/** Undo window length. @default {@link TIMED_UNDO_DEFAULT_DURATION_MS} */
	durationMs?: number;
	/**
	 * Called when the window expires, when a new {@link UseTimedUndoResult.schedule}
	 * replaces a pending item, or on unmount while pending.
	 */
	onCommit: (payload: T) => void | Promise<void>;
}

export interface UseTimedUndoResult<T> {
	pending: TimedUndoPending<T> | null;
	/** Remaining ms in the active window (0 when idle). */
	remainingMs: number;
	/** 0–100 countdown progress (100 at start → 0 at expiry). */
	remainingPercent: number;
	durationMs: number;
	/**
	 * Optimistically start an undo window. Commits any previous pending payload
	 * first (Gmail-style single pending action).
	 */
	schedule: (payload: T) => void;
	/** Cancel the window and return the payload (caller restores UI). */
	undo: () => T | null;
	/** Commit immediately without waiting for the timer. */
	commitNow: () => Promise<void>;
}

/**
 * Single-slot timed undo: schedule → optional undo within `durationMs` → else commit.
 * Decoupled from UI — pair with {@link TimedUndoBar}.
 */
export function useTimedUndo<T>({
	durationMs = TIMED_UNDO_DEFAULT_DURATION_MS,
	onCommit,
}: UseTimedUndoOptions<T>): UseTimedUndoResult<T> {
	const [pending, setPending] = useState<TimedUndoPending<T> | null>(null);
	const pendingRef = useRef(pending);
	pendingRef.current = pending;

	const onCommitRef = useRef(onCommit);
	onCommitRef.current = onCommit;

	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const tickSubscribersRef = useRef(new Set<() => void>());
	const nowRef = useRef(Date.now());

	const clearTimer = useCallback(() => {
		if (timerRef.current != null) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const notifyTick = useCallback(() => {
		nowRef.current = Date.now();
		for (const sub of tickSubscribersRef.current) {
			sub();
		}
	}, []);

	const commitPayload = useCallback(async (payload: T) => {
		await onCommitRef.current(payload);
	}, []);

	const commitPendingInternal = useCallback(async () => {
		const current = pendingRef.current;
		if (!current) {
			return;
		}
		clearTimer();
		setPending(null);
		pendingRef.current = null;
		await commitPayload(current.payload);
	}, [clearTimer, commitPayload]);

	const schedule = useCallback(
		(payload: T) => {
			const previous = pendingRef.current;
			clearTimer();
			if (previous) {
				void commitPayload(previous.payload);
			}
			const startedAt = Date.now();
			const next: TimedUndoPending<T> = {
				payload,
				startedAt,
				endsAt: startedAt + durationMs,
			};
			pendingRef.current = next;
			setPending(next);
			notifyTick();
			timerRef.current = setTimeout(() => {
				timerRef.current = null;
				const still = pendingRef.current;
				if (!still || still.startedAt !== startedAt) {
					return;
				}
				pendingRef.current = null;
				setPending(null);
				void commitPayload(still.payload);
			}, durationMs);
		},
		[clearTimer, commitPayload, durationMs, notifyTick],
	);

	const undo = useCallback(() => {
		const current = pendingRef.current;
		if (!current) {
			return null;
		}
		clearTimer();
		pendingRef.current = null;
		setPending(null);
		notifyTick();
		return current.payload;
	}, [clearTimer, notifyTick]);

	const commitNow = useCallback(async () => {
		await commitPendingInternal();
		notifyTick();
	}, [commitPendingInternal, notifyTick]);

	// Tick while pending so remainingMs/percent stay live.
	useEffect(() => {
		if (!pending) {
			return;
		}
		const id = setInterval(notifyTick, 50);
		return () => clearInterval(id);
	}, [pending, notifyTick]);

	// Commit on unmount so optimistic deletes are not lost.
	useEffect(() => {
		return () => {
			clearTimer();
			const current = pendingRef.current;
			if (current) {
				pendingRef.current = null;
				void commitPayload(current.payload);
			}
		};
	}, [clearTimer, commitPayload]);

	const now = useSyncExternalStore(
		(onStoreChange) => {
			tickSubscribersRef.current.add(onStoreChange);
			return () => {
				tickSubscribersRef.current.delete(onStoreChange);
			};
		},
		() => nowRef.current,
		() => 0,
	);

	const remainingMs = pending
		? Math.max(0, pending.endsAt - now)
		: 0;
	const remainingPercent = pending
		? Math.max(0, Math.min(100, (remainingMs / durationMs) * 100))
		: 0;

	return {
		pending,
		remainingMs,
		remainingPercent,
		durationMs,
		schedule,
		undo,
		commitNow,
	};
}
